import React from 'react';
import Head from 'next/head';
import NextSeo from 'next-seo';
import { NextPage, NextPageContext } from 'next';

import { useTranslations } from '@lang';
import { TablesVersion } from '@features/tables/pages/Tables/TablesVersion';

import { Router, useRouter } from 'shared-routing';

import * as entityActions from '@store/entity/actions';
import { Property } from 'shared-types/Property';
import { Store } from 'redux';
import { useLabels } from '@features/Menu/useLabels';
import { changeJourneyParam } from '@store/yourJourney/actions';

type TablesPageProps = {
  propertyTitle: string;
  language: string;
  url: string;
  socialFallbackImage: { url: string };
};

const TablesPage: NextPage<TablesPageProps> = ({
  propertyTitle,
  language,
  url = '',
  socialFallbackImage,
}) => {
  const { t } = useTranslations();
  const labels = useLabels();
  const router = useRouter();
  const openModal = router.pathname.split('/').includes('book');
  const { restaurant } = router.query;

  const pageName = labels?.tables || t('app.dining');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <TablesVersion
        language={language}
        openModal={openModal}
        openModalWith={{ value: restaurant }}
      />
    </>
  );
};

TablesPage.getInitialProps = async ({
  property,
  store,
  language,
  url,
  res,
}: NextPageContext & {
  property: Property;
  store: Store;
  language: string;
  url: string;
}): Promise<TablesPageProps> => {
  if (url) {
    const urlQuery = new URL(url).searchParams;

    const adults = urlQuery.get('adults');
    const children = urlQuery.get('children');
    const infants = urlQuery.get('infants');

    if (adults) {
      store.dispatch(changeJourneyParam('tableAdults', adults));
    }

    if (children) {
      store.dispatch(changeJourneyParam('tableChildren', children));
    }

    if (infants) {
      store.dispatch(changeJourneyParam('tableChildren', infants));
    }
  }

  if (!property.isTablesIntegrated) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }

  await store.dispatch(entityActions.fetchTablesPage(property.id, language));

  return {
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
    language,
    url,
  };
};

export default TablesPage;
